import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Button,
  Alert,
  Input,
  VStack,
  NativeSelectRoot,
  NativeSelectField,
  NativeSelectIndicator,
  Text,
  Flex,
  Checkbox,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { useTranslation } from 'react-i18next'
import { useState, useMemo, useEffect } from 'react'
import { useInviteUser } from '../hooks/useUsers'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import { useSubscription } from '@/shared/hooks/useSubscription'
import { UpgradeModal } from '@/shared/components/UpgradeModal'
import { AlertTriangle } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useProperties } from '@/features/properties/hooks/useProperties'

interface UserInviteModalProps {
  isOpen: boolean
  onClose: () => void
}

export function UserInviteModal({ isOpen, onClose }: UserInviteModalProps) {
  const { t } = useTranslation()
  const inviteUser = useInviteUser()
  const { data: agency } = useAgency()
  const { data: authData } = useAuth()
  const { data: residences = [] } = useProperties()
  const { canAddUser, usage, limits, isNearLimit } = useSubscription()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'USER' as 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'USER',
  })
  const [selectedResidenceIds, setSelectedResidenceIds] = useState<string[]>([])

  // Déterminer si l'utilisateur connecté est ADMIN
  const currentUserIsAdmin = authData?.isAdmin || false

  // Pré-sélectionner toutes les résidences quand ACCOUNTANT est sélectionné
  useEffect(() => {
    if (formData.role === 'ACCOUNTANT' && residences.length > 0 && selectedResidenceIds.length === 0) {
      setSelectedResidenceIds(residences.map((r) => r.id))
    }
  }, [formData.role, residences, selectedResidenceIds.length])

  // Filtrer les rôles disponibles selon le rôle de l'utilisateur connecté
  const availableRoles = useMemo(() => {
    if (currentUserIsAdmin) {
      // ADMIN peut inviter tous les rôles
      return [
        { value: 'USER', label: 'Utilisateur' },
        { value: 'ACCOUNTANT', label: 'Comptable' },
        { value: 'MANAGER', label: 'Gestionnaire' },
        { value: 'ADMIN', label: 'Administrateur' },
      ]
    } else {
      // MANAGER ne peut inviter que des USER
      return [{ value: 'USER', label: 'Utilisateur' }]
    }
  }, [currentUserIsAdmin])

  // Tous les rôles nécessitent au moins une résidence
  const needsResidenceSelection = true

  // Gérer la sélection/désélection de résidences
  const toggleResidence = (residenceId: string) => {
    setSelectedResidenceIds((prev) =>
      prev.includes(residenceId)
        ? prev.filter((id) => id !== residenceId)
        : [...prev, residenceId]
    )
  }

  const toggleAllResidences = () => {
    if (selectedResidenceIds.length === residences.length) {
      setSelectedResidenceIds([])
    } else {
      setSelectedResidenceIds(residences.map((r) => r.id))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!agency?.id) {
      console.error('Agency ID not found')
      alert(t('common.error'))
      return
    }

    // Vérifier que des résidences sont sélectionnées si nécessaire
    if (needsResidenceSelection && selectedResidenceIds.length === 0) {
      alert('Veuillez sélectionner au moins une résidence')
      return
    }

    // Vérifier la limite avant de créer
    if (!canAddUser()) {
      setShowUpgradeModal(true)
      return
    }

    try {
      // Si MANAGER invite un USER, on envoie un tableau vide
      // La edge function détectera que c'est un MANAGER et ajoutera automatiquement ses résidences
      const residenceIds = currentUserIsAdmin ? selectedResidenceIds : []

      await inviteUser.mutateAsync({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        residenceIds,
        agencyId: agency.id,
      })
      onClose()
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'USER',
      })
      setSelectedResidenceIds([])
    } catch (error) {
      console.error('Error inviting user:', error)
      const errorMessage = error instanceof Error ? error.message : t('common.error')
      alert(errorMessage)
    }
  }

  return (
    <>
      <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="lg">
        <DialogBackdrop />
        <DialogPositioner
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="fixed"
          inset="0"
        >
          <DialogContent margin="auto" maxWidth="600px" maxHeight="85vh" width="full">
            <DialogHeader>
              <DialogTitle>Inviter un utilisateur</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody pb={6} overflowY="auto" maxHeight="calc(85vh - 180px)">
              {/* Afficher warning si proche de la limite */}
              {usage && limits && limits.users !== -1 && isNearLimit('users') && (
                <Alert.Root status="warning" mb={4}>
                  <Alert.Indicator>
                    <AlertTriangle />
                  </Alert.Indicator>
                  <Alert.Content>
                    <Alert.Title>Attention</Alert.Title>
                    <Alert.Description>
                      Vous avez utilisé {usage.users}/{limits.users} utilisateurs. Pensez à
                      mettre à niveau votre formule.
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}

              <form id="user-invite-form" onSubmit={handleSubmit}>
                <VStack gap={4} align="stretch">
                  <Field label="Email" required>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="utilisateur@example.com"
                      required
                    />
                  </Field>

                  <Field label="Prénom" required>
                    <Input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Prénom"
                      required
                    />
                  </Field>

                  <Field label="Nom" required>
                    <Input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Nom"
                      required
                    />
                  </Field>

                  <Field label="Rôle" required>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            role: e.target.value as 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'USER',
                          })
                        }
                      >
                        {availableRoles.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </NativeSelectField>
                      <NativeSelectIndicator />
                    </NativeSelectRoot>
                  </Field>

                  {/* Sélection des résidences - uniquement pour ADMIN */}
                  {currentUserIsAdmin && (
                    <Field
                      label="Résidences (requis)"
                      helperText={
                        formData.role === 'ACCOUNTANT'
                          ? 'Toutes les résidences sont pré-sélectionnées pour le comptable. Vous pouvez décocher si nécessaire.'
                          : 'Sélectionnez les résidences auxquelles cet utilisateur aura accès'
                      }
                    >
                      <VStack align="stretch" gap={2}>
                        {residences.length > 0 && (
                          <Flex align="center" gap={2} borderBottomWidth="1px" pb={2}>
                            <Checkbox.Root
                              checked={selectedResidenceIds.length === residences.length}
                              onCheckedChange={toggleAllResidences}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label>
                                <Text fontWeight="medium">
                                  Toutes les résidences ({residences.length})
                                </Text>
                              </Checkbox.Label>
                            </Checkbox.Root>
                          </Flex>
                        )}
                        {residences.map((residence) => (
                          <Checkbox.Root
                            key={residence.id}
                            checked={selectedResidenceIds.includes(residence.id)}
                            onCheckedChange={() => toggleResidence(residence.id)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label>{residence.name}</Checkbox.Label>
                          </Checkbox.Root>
                        ))}
                        {residences.length === 0 && (
                          <Text color="gray.500" fontSize="sm">
                            Aucune résidence disponible
                          </Text>
                        )}
                      </VStack>
                    </Field>
                  )}

                  {/* Message pour MANAGER */}
                  {!currentUserIsAdmin && (
                    <Alert.Root status="info">
                      <Alert.Indicator>
                        <AlertTriangle />
                      </Alert.Indicator>
                      <Alert.Content>
                        <Alert.Description>
                          L'utilisateur sera automatiquement associé à toutes vos résidences
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                </VStack>
              </form>
            </DialogBody>
            <DialogFooter
              position="sticky"
              bottom={0}
              bg="white"
              borderTopWidth="1px"
              display="flex"
              gap={3}
              justifyContent="flex-end"
              p={4}
            >
              <Button variant="outline" onClick={onClose} disabled={inviteUser.isPending}>
                Annuler
              </Button>
              <Button
                type="submit"
                form="user-invite-form"
                colorPalette="brand"
                loading={inviteUser.isPending}
              >
                Envoyer l'invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>

        {/* Modal d'upgrade si limite atteinte */}
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          reason={`Vous avez atteint la limite de ${limits?.users} utilisateurs de votre formule actuelle.`}
          resource="users"
        />
      </DialogRoot>
    </>
  )
}
